import { Badge } from '@/components/ui/badge';
import { EntityLink } from '@/components/ui/entity-link';
import { useEntityModalContext } from '@/contexts/EntityModalContext';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDate } from '@/pages/contracts/utils';

interface ContractDisplayItemProps {
	contractId: string;
	contractNumber?: string | null;
	beginDate?: string | null;
	endDate?: string | null;
	amount?: number | null;
	simultaneousVehicleLimit?: number | null;
	isActive?: boolean | null;
	inactiveLabel?: string;
	className?: string;
}

export function ContractDisplayItem({
	contractId,
	contractNumber,
	beginDate,
	endDate,
	amount,
	simultaneousVehicleLimit,
	isActive,
	inactiveLabel = 'Inactive',
	className
}: ContractDisplayItemProps) {
	const { openEntityModal } = useEntityModalContext();
	const periodValue = `${formatDate(beginDate) ?? '-'} - ${formatDate(endDate) ?? '-'}`;
	const amountValue = formatCurrency(amount ?? undefined) ?? '-';
	const vehicleLimitValue = simultaneousVehicleLimit != null
		? `${simultaneousVehicleLimit} vehicles`
		: null;

	return (
		<div className={cn('bg-transparent p-4', className)}>
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div className="min-w-0">
							<EntityLink
								entityId={contractId}
								entityType="contracts"
								displayText={contractNumber || `#${contractId.slice(-6)}`}
								onEntityClick={openEntityModal}
								className="text-sm font-semibold text-primary"
							/>
						</div>
						{isActive === false && (
							<Badge
								variant="outline"
								className="shrink-0 border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200"
							>
								{inactiveLabel}
							</Badge>
						)}
					</div>
					<p className="mt-1 text-sm text-muted-foreground">
						{[periodValue, amountValue].join(' - ')}
					</p>
					{vehicleLimitValue && (
						<div className="mt-2 flex flex-wrap items-center gap-2">
							<Badge variant="outline">{vehicleLimitValue}</Badge>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
